"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SimpleContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 flex flex-col items-center text-center space-y-6 bg-neutral-900 border border-white/10 rounded-lg">
        <div className="size-16 bg-primary flex items-center justify-center rounded-md">
          <ArrowRight className="size-8 text-black" />
        </div>
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">
            STATUS: VERZONDEN
          </p>
          <h3 className="font-default uppercase text-3xl font-bold leading-none text-white">
            Bericht Ontvangen
          </h3>
        </div>
        <p className="text-neutral-400 leading-relaxed max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black font-mono uppercase tracking-widest text-xs h-12 px-8 rounded-md">
          Nieuw Bericht
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="space-y-10">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                01. Naam
              </FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} className="border-0 border-b border-white/20 bg-transparent text-white h-10 px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors placeholder:text-white/10 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mailadres is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                02. E-mailadres
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="uw@email.nl" {...field} className="border-0 border-b border-white/20 bg-transparent text-white h-10 px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors placeholder:text-white/10 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                03. Bericht
              </FormLabel>
              <FormControl>
                <Textarea rows={4} placeholder="Hoe kunnen we u helpen?" {...field} className="border border-white/10 bg-white/5 text-white px-4 py-3 focus-visible:ring-0 focus-visible:border-primary transition-colors resize-none rounded-md placeholder:text-white/10" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-md">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-8 border-t border-white/10">
        <div className="flex items-center gap-2">
          <div className="size-1.5 bg-primary animate-pulse rounded-lg" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Gemiddelde reactietijd: 24u
          </p>
        </div>
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-black hover:bg-primary/90 font-default uppercase tracking-widest text-xs font-bold h-14 px-10 rounded-md shadow-lg">
          {isSubmitting ? "Verzenden..." : "Verstuur Bericht"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const formReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const infoRows = [{
    label: "Bereikbaarheid",
    value: "Ma - Vr / 09:00 - 17:00"
  }, {
    label: "Locatie",
    value: "Amsterdam, NL"
  }, {
    label: "Reactietijd",
    value: "Binnen 24 Uur"
  }];
  return <section id="contact" className="bg-background text-foreground py-24 md:py-40 border-t border-white/10">
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        <div ref={headerReveal.ref} className={`mb-16 md:mb-24 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          <h2 className="font-default font-bold text-white uppercase mb-6" style={{
          fontSize: 'clamp(2.5rem, 8vw, 5.5rem)',
          letterSpacing: '-0.04em',
          lineHeight: '1.0'
        }}>
            KLAAR OM TE <span className="text-primary">GROEIEN?</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl text-lg leading-relaxed">
            Neem contact op voor een vrijblijvende strategie sessie. We analyseren je huidige positie en bouwen de motor voor je volgende groeifase.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div ref={formReveal.ref} className={`lg:col-span-7 transition-all duration-700 ease-out delay-100 ${formReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <FormProvider className="w-full max-w-2xl mx-auto" formId="contact_form_simple" sectionName="contact-section" successMessage="Bedankt, we nemen snel contact op!" defaultValues={{
            name: "",
            email: "",
            message: ""
          }}>
              <SimpleContactForm />
            </FormProvider>
          </div>

          <div className="lg:col-span-5 space-y-12">
            
          </div>
        </div>
      </div>
    </section>;
}